import { useState, useCallback, useEffect, useRef } from 'react';
import { FormSection, FormField, DeveloperNote, BlueprintData } from './types';
import { GlossaryBlueprint } from '@/types-games';
import { generateUUID, generateTempId } from '@/lib/utils/uuid';
import { parseBlueprintData, convertBlueprintDataToSections } from '@/lib/utils/blueprintDataParser';
import { TemplateDraftData } from '@/lib/stores/wizardDraftStore';

interface UseBlueprintStateProps {
  initialData?: GlossaryBlueprint;
  draftData?: TemplateDraftData | null;
}

export function useBlueprintState({ initialData, draftData }: UseBlueprintStateProps) {
  // Track if we've initialized from draft (to only do it once)
  const hasInitializedFromDraftRef = useRef(false);

  // Determine initial values - prefer draft data over initialData for new items
  const getInitialValue = <T,>(draftValue: T | undefined, initialValue: T, defaultValue: T): T => {
    // If we have draft data and it's for a new item (no initialData), use draft
    if (draftData && !initialData) {
      return draftValue !== undefined ? draftValue : defaultValue;
    }
    // Otherwise use initialData or default
    return initialValue !== undefined ? initialValue : defaultValue;
  };

  // Basic blueprint info
  const [blueprintName, setBlueprintName] = useState(() => 
    getInitialValue(draftData?.name, initialData?.name, '')
  );
  const [blueprintDescription, setBlueprintDescription] = useState(() =>
    getInitialValue(draftData?.description, initialData?.description, '')
  );
  const [blueprintColor, setBlueprintColor] = useState(() =>
    getInitialValue(draftData?.color, initialData?.settings?.color, '#6E35FF')
  );
  const [itemTags, setItemTags] = useState<string[]>(() =>
    getInitialValue(draftData?.tags, initialData?.tags, [])
  );
  const [itemTypes, setItemTypes] = useState<string[]>(() =>
    getInitialValue(draftData?.types, initialData?.types, [])
  );
  const [owner, setOwner] = useState<string | null>(() =>
    getInitialValue(draftData?.owner, initialData?.owner, null)
  );
  const [editPermissions, setEditPermissions] = useState<'Open' | 'Locked'>(() =>
    getInitialValue(draftData?.editPermissions, initialData?.edit_permissions, 'Open')
  );
  const [blueprintStatus, setBlueprintStatus] = useState<string>(() =>
    getInitialValue(draftData?.status, initialData?.status, 'draft')
  );

  // Sections and fields
  const [sections, setSections] = useState<FormSection[]>([]);
  const [developerNotes, setDeveloperNotes] = useState<DeveloperNote[]>([]);
  const [renderOrder, setRenderOrder] = useState<Array<{ type: 'section' | 'note'; id: string }>>([]);
  const [lastAddedSectionId, setLastAddedSectionId] = useState<string | undefined>(undefined);
  const [lastAddedFieldId, setLastAddedFieldId] = useState<string | undefined>(undefined);

  // Convert blueprint data to sections using the utility function
  const convertBlueprintDataToSectionsLocal = useCallback((blueprint: GlossaryBlueprint) => {
    const result = convertBlueprintDataToSections(blueprint);
    return {
      sections: result.sections as FormSection[],
      notes: result.notes as DeveloperNote[],
      order: result.order,
    };
  }, []);

  // Initialize sections from initialData or draftData
  useEffect(() => {
    if (initialData) {
      const { sections: initialSections, notes: initialNotes, order: initialOrder } = convertBlueprintDataToSectionsLocal(initialData);
      setSections(initialSections);
      setDeveloperNotes(initialNotes);
      setRenderOrder(initialOrder);
    } else if (draftData && draftData.sections && draftData.sections.length > 0 && !hasInitializedFromDraftRef.current) {
      // Initialize from draft data (for new items with saved draft)
      console.log('[useBlueprintState] Initializing from draft data:', draftData);
      hasInitializedFromDraftRef.current = true;
      setSections(draftData.sections as FormSection[]);
      
      // Reconstruct renderOrder from sections
      const draftRenderOrder = (draftData.renderOrder || []).map(id => {
        if (typeof id === 'string') {
          // Check if it's a section or note
          if (id.startsWith('DevNode_')) {
            return { type: 'note' as const, id: id.replace('DevNode_', '') };
          }
          return { type: 'section' as const, id };
        }
        return id;
      });
      
      if (draftRenderOrder.length > 0) {
        setRenderOrder(draftRenderOrder as Array<{ type: 'section' | 'note'; id: string }>);
      } else {
        // If no render order in draft, create from sections
        setRenderOrder(draftData.sections.map((s: any) => ({ type: 'section', id: s.id })));
      }
      
      setDeveloperNotes([]);
    } else if (!initialData && !hasInitializedFromDraftRef.current) {
      // Default initialization for new blueprints (no draft available)
      const defaultSectionId = generateUUID();
      const defaultFieldId = generateUUID();
      setSections([{
        id: defaultSectionId,
        title: 'New Section',
        fields: [{
          id: defaultFieldId,
          type: 'title',
          label: 'Text Field',
          mandatory: false,
          translation: false,
          multiple: false,
          filter: false,
        }],
        fieldOrder: [defaultFieldId], // Add fieldOrder
      }]);
      setDeveloperNotes([]);
      setRenderOrder([{ type: 'section', id: defaultSectionId }]);
    }
  }, [initialData, draftData, convertBlueprintDataToSectionsLocal]);

  // Function to reset sections to initial data (for discarding changes)
  const resetToInitialData = useCallback(() => {
    if (initialData) {
      const { sections: initialSections, notes: initialNotes, order: initialOrder } = convertBlueprintDataToSectionsLocal(initialData);
      setSections(initialSections);
      setDeveloperNotes(initialNotes);
      setRenderOrder(initialOrder);
    }
  }, [initialData, convertBlueprintDataToSectionsLocal]);

  // Update basic fields when initialData changes
  useEffect(() => {
    if (initialData) {
      setBlueprintName(initialData.name || '');
      setBlueprintDescription(initialData.description || '');
      setBlueprintColor(initialData.settings?.color || '#6E35FF');
      setItemTags(initialData.tags || []);
      setItemTypes(initialData.types || []);
      setOwner(initialData.owner || null);
      setEditPermissions(initialData.edit_permissions || 'Open');
      setBlueprintStatus(initialData.status || 'draft');
    }
  }, [initialData]);

  // UI state
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);

  // Section management
  const addSection = useCallback(() => {
    const newSectionId = generateUUID();
    const newFieldId = generateUUID();
    const newSection: FormSection = {
      id: newSectionId,
      title: 'New Section',
      fields: [{
        id: newFieldId,
        type: 'title', // Use 'title' instead of 'text'
        label: 'Text Field',
        mandatory: false,
        translation: false,
        multiple: false,
        filter: false,
      }],
      fieldOrder: [newFieldId], // Add fieldOrder
    };

    setSections(prev => [...prev, newSection]);
    setRenderOrder(prev => [...prev, { type: 'section', id: newSectionId }]);
    setLastAddedSectionId(newSectionId); // Track the newly added section
  }, []);

  const deleteSection = useCallback((sectionId: string) => {
    // Prevent deletion of the last section
    setSections(prev => {
      if (prev.length <= 1) {
        return prev; // Don't delete if it's the last section
      }
      return prev.filter(section => section.id !== sectionId);
    });
    setRenderOrder(prev => {
      // Only remove from order if we actually deleted the section
      const willDelete = sections.length > 1;
      return willDelete ? prev.filter(item => item.id !== sectionId) : prev;
    });
  }, [sections.length]);

  const updateSection = useCallback((sectionId: string, updatedSection: FormSection) => {
    setSections(prev => prev.map(section => 
      section.id === sectionId ? updatedSection : section
    ));
  }, []);

  // Field management
  const addField = useCallback((sectionId: string, fieldType: string) => {
    const newFieldId = generateUUID();
    const newField: FormField = {
      id: newFieldId,
      type: fieldType as any,
      label: `New ${fieldType} Field`,
      mandatory: false,
      translation: false,
      multiple: false,
      filter: false,
    };

    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { 
            ...section, 
            fields: [...section.fields, newField],
            fieldOrder: [...section.fieldOrder, newField.id]
          }
        : section
    ));
    
    // Track the newly added field for autofocus
    setLastAddedFieldId(newFieldId);
  }, []);

  const deleteField = useCallback((sectionId: string, fieldId: string) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { 
            ...section, 
            fields: section.fields.filter(field => field.id !== fieldId),
            fieldOrder: section.fieldOrder.filter(id => id !== fieldId)
          }
        : section
    ));
  }, []);

  const updateField = useCallback((sectionId: string, fieldId: string, updatedField: FormField) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? {
            ...section,
            fields: section.fields.map(field =>
              field.id === fieldId ? updatedField : field
            ),
          }
        : section
    ));
  }, []);

  // Role visibility management
  const toggleSectionForRole = useCallback((sectionId: string, roleId: string) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        const currentHidden = section.hiddenForRoles || [];
        const isHidden = currentHidden.includes(roleId);
        
        if (isHidden) {
          // Remove role from section and all its fields
          return {
            ...section,
            hiddenForRoles: currentHidden.filter(r => r !== roleId),
            fields: section.fields.map(field => ({
              ...field,
              hiddenForRoles: (field.hiddenForRoles || []).filter(r => r !== roleId),
            })),
          };
        } else {
          // Add role to section and all its fields
          return {
            ...section,
            hiddenForRoles: [...currentHidden, roleId],
            fields: section.fields.map(field => ({
              ...field,
              hiddenForRoles: [...(field.hiddenForRoles || []), roleId],
            })),
          };
        }
      }
      return section;
    }));
  }, []);

  const toggleFieldForRole = useCallback((sectionId: string, fieldId: string, roleId: string) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        const updatedFields = section.fields.map(field => {
          if (field.id === fieldId) {
            const currentHidden = field.hiddenForRoles || [];
            const isHidden = currentHidden.includes(roleId);
            
            return {
              ...field,
              hiddenForRoles: isHidden
                ? currentHidden.filter(r => r !== roleId)
                : [...currentHidden, roleId],
            };
          }
          return field;
        });

        // Check if all fields are now visible for this role
        const allFieldsVisible = updatedFields.every(field => 
          !(field.hiddenForRoles || []).includes(roleId)
        );

        // If all fields are visible, ensure section is also visible
        if (allFieldsVisible) {
          return {
            ...section,
            fields: updatedFields,
            hiddenForRoles: (section.hiddenForRoles || []).filter(r => r !== roleId),
          };
        }

        return {
          ...section,
          fields: updatedFields,
        };
      }
      return section;
    }));
  }, []);

  const updateSectionVisibility = useCallback((sectionId: string, roleIds: string[]) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { ...section, hiddenForRoles: roleIds }
        : section
    ));
  }, []);

  const updateFieldVisibility = useCallback((sectionId: string, fieldId: string, roleIds: string[]) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? {
            ...section,
            fields: section.fields.map(field =>
              field.id === fieldId
                ? { ...field, hiddenForRoles: roleIds }
                : field
            ),
          }
        : section
    ));
  }, []);

  // New 3-state visibility management functions
  const setSectionVisibilityState = useCallback((
    sectionId: string, 
    roleId: string, 
    state: 'visible-editable' | 'visible-readonly' | 'hidden'
  ) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        const hiddenRoles = section.hiddenForRoles || [];
        const readOnlyRoles = section.readOnlyForRoles || [];
        
        // Remove role from both arrays first
        const filteredHidden = hiddenRoles.filter(r => r !== roleId);
        const filteredReadOnly = readOnlyRoles.filter(r => r !== roleId);
        
        let newHiddenForRoles = filteredHidden;
        let newReadOnlyForRoles = filteredReadOnly;
        
        if (state === 'hidden') {
          newHiddenForRoles = [...filteredHidden, roleId];
        } else if (state === 'visible-readonly') {
          newReadOnlyForRoles = [...filteredReadOnly, roleId];
        }
        // visible-editable means not in either array
        
        // Cascade to all fields
        const updatedFields = section.fields.map(field => {
          const fieldHidden = field.hiddenForRoles || [];
          const fieldReadOnly = field.readOnlyForRoles || [];
          
          const filteredFieldHidden = fieldHidden.filter(r => r !== roleId);
          const filteredFieldReadOnly = fieldReadOnly.filter(r => r !== roleId);
          
          return {
            ...field,
            hiddenForRoles: state === 'hidden' ? [...filteredFieldHidden, roleId] : filteredFieldHidden,
            readOnlyForRoles: state === 'visible-readonly' ? [...filteredFieldReadOnly, roleId] : filteredFieldReadOnly,
          };
        });
        
        return {
          ...section,
          hiddenForRoles: newHiddenForRoles,
          readOnlyForRoles: newReadOnlyForRoles,
          fields: updatedFields,
        };
      }
      return section;
    }));
  }, []);

  const setFieldVisibilityState = useCallback((
    sectionId: string, 
    fieldId: string, 
    roleId: string, 
    state: 'visible-editable' | 'visible-readonly' | 'hidden'
  ) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        const updatedFields = section.fields.map(field => {
          if (field.id === fieldId) {
            const hiddenRoles = field.hiddenForRoles || [];
            const readOnlyRoles = field.readOnlyForRoles || [];
            
            // Remove role from both arrays first
            const filteredHidden = hiddenRoles.filter(r => r !== roleId);
            const filteredReadOnly = readOnlyRoles.filter(r => r !== roleId);
            
            let newHiddenForRoles = filteredHidden;
            let newReadOnlyForRoles = filteredReadOnly;
            
            if (state === 'hidden') {
              newHiddenForRoles = [...filteredHidden, roleId];
            } else if (state === 'visible-readonly') {
              newReadOnlyForRoles = [...filteredReadOnly, roleId];
            }
            
            return {
              ...field,
              hiddenForRoles: newHiddenForRoles,
              readOnlyForRoles: newReadOnlyForRoles,
            };
          }
          return field;
        });
        
        // Check if all fields are visible-editable
        const allFieldsVisibleEditable = updatedFields.every(field => {
          const fieldHidden = field.hiddenForRoles || [];
          const fieldReadOnly = field.readOnlyForRoles || [];
          return !fieldHidden.includes(roleId) && !fieldReadOnly.includes(roleId);
        });
        
        // If all fields are visible-editable, make section visible-editable too
        if (allFieldsVisibleEditable) {
          const sectionHidden = section.hiddenForRoles || [];
          const sectionReadOnly = section.readOnlyForRoles || [];
          
          return {
            ...section,
            fields: updatedFields,
            hiddenForRoles: sectionHidden.filter(r => r !== roleId),
            readOnlyForRoles: sectionReadOnly.filter(r => r !== roleId),
          };
        }
        
        return {
          ...section,
          fields: updatedFields,
        };
      }
      return section;
    }));
  }, []);

  // Developer notes management
  const addDeveloperNote = useCallback(() => {
    const newNoteId = generateUUID();
    const newNote: DeveloperNote = {
      id: newNoteId,
      color: '#FF6B6B',
      note: 'New developer note...',
    };

    setDeveloperNotes(prev => [...prev, newNote]);
    setRenderOrder(prev => [...prev, { type: 'note', id: newNoteId }]);
  }, []);

  const deleteDeveloperNote = useCallback((noteId: string) => {
    setDeveloperNotes(prev => prev.filter(note => note.id !== noteId));
    setRenderOrder(prev => prev.filter(item => item.id !== noteId));
  }, []);

  const updateDeveloperNote = useCallback((noteId: string, updatedNote: DeveloperNote) => {
    setDeveloperNotes(prev => prev.map(note =>
      note.id === noteId ? updatedNote : note
    ));
  }, []);

  // Convert to blueprint data format
  const getBlueprintData = useCallback((): BlueprintData => {
    const sectionsData: Record<string, any> = {};
    const devnotesData: Record<string, any> = {};
    const elementOrder: string[] = [];

    // Use renderOrder to maintain the correct order
    renderOrder.forEach(orderItem => {
      if (orderItem.type === 'section') {
        const section = sections.find(s => s.id === orderItem.id);
        if (section) {
          elementOrder.push(section.id); // Use section ID instead of title
          
          if (section.fields.length === 1) {
            // Single field section
            const field = section.fields[0];
            sectionsData[section.id] = { // Use section ID as key instead of title
              id: field.id,
              label: field.label,
              type: field.type,
              req: field.mandatory,
              trans: field.translation,
              multiple: field.multiple,
              filter: field.filter,
              filterOptions: field.filterOptions,
              _sectionTitle: section.title,
              _sectionDocumentation: section.documentation,
              documentation: field.documentation,
              hiddenForRoles: field.hiddenForRoles || [], // Save field role visibility
            };
          } else if (section.fields.length > 1) {
            // Multiple fields section - preserve field order
            const sectionData: Record<string, any> = {};
            const fieldOrder: string[] = [];
            
            // Use fieldOrder if available, otherwise use current order
            const orderedFieldIds = section.fieldOrder || section.fields.map(f => f.id);

            
            // First, add all fields to sectionData using field IDs as keys
            section.fields.forEach(field => {
              sectionData[field.id] = { // Use field ID as key instead of label
                id: field.id,
                label: field.label,
                type: field.type,
                req: field.mandatory,
                trans: field.translation,
                multiple: field.multiple,
                filter: field.filter,
                filterOptions: field.filterOptions,
                documentation: field.documentation,
                hiddenForRoles: field.hiddenForRoles || [], // Save field role visibility
                readOnlyForRoles: field.readOnlyForRoles || [], // Save field readonly state
              };
            });
            
            // Then, build the fieldOrder array based on the ordered IDs
            orderedFieldIds.forEach(fieldId => {
              const field = section.fields.find(f => f.id === fieldId);
              if (field) {
                fieldOrder.push(field.id); // Use field ID instead of label
              }
            });
            
            sectionsData[section.id] = { // Use section ID as key instead of title
              ...sectionData,
              _fieldOrder: fieldOrder,
              _sectionTitle: section.title,
              _sectionDocumentation: section.documentation,
              hiddenForRoles: section.hiddenForRoles || [], // Save section role visibility
              readOnlyForRoles: section.readOnlyForRoles || [], // Save section readonly state
            };
          } else {
            // No fields in section
            sectionsData[section.id] = {
              _sectionTitle: section.title,
              _sectionDocumentation: section.documentation,
              hiddenForRoles: section.hiddenForRoles || [], // Save section role visibility even for empty sections
              readOnlyForRoles: section.readOnlyForRoles || [], // Save section readonly state even for empty sections
            };
          }
        }
      } else if (orderItem.type === 'note') {
        const note = developerNotes.find(n => n.id === orderItem.id);
        if (note) {
          devnotesData[`DevNode_${note.id}`] = {
            color: note.color,
            note: note.note,
          };
          elementOrder.push(`DevNode_${note.id}`);
        }
      }
    });

    const result = { 
      sections: sectionsData,
      devnotes: devnotesData,
      elementOrder: elementOrder
    };
    return result;
  }, [sections, developerNotes, renderOrder]);

  // Reset form to initial data or clear it
  const resetForm = useCallback(() => {
    if (initialData) {
      // Reset to initial data
      setBlueprintName(initialData.name || '');
      setBlueprintDescription(initialData.description || '');
      setBlueprintColor(initialData.settings?.color || '#6E35FF');
      setItemTags(initialData.tags || []);
      setItemTypes(initialData.types || []);
      setOwner(initialData.owner || null);
      setEditPermissions(initialData.edit_permissions || 'Open');
      setBlueprintStatus(initialData.status || 'draft');

      const { sections: initialSections, notes: initialNotes, order: initialOrder } = convertBlueprintDataToSectionsLocal(initialData);
      setSections(initialSections);
      setDeveloperNotes(initialNotes);
      setRenderOrder(initialOrder);
    } else {
      // Clear form (for new blueprints)
      setBlueprintName('');
      setBlueprintDescription('');
      setBlueprintColor('#6E35FF');
      setItemTags([]);
      setItemTypes([]);
      setOwner(null);
      setEditPermissions('Open');
      setBlueprintStatus('draft');

      const defaultSectionId = generateUUID();
      const defaultFieldId = generateUUID();
      setSections([{
        id: defaultSectionId,
        title: 'New Section',
        fields: [{
          id: defaultFieldId,
          type: 'title', // Use 'title' instead of 'text'
          label: 'Text Field',
          mandatory: false,
          translation: false,
          multiple: false,
          filter: false,
        }],
        fieldOrder: [defaultFieldId], // Add fieldOrder
      }]);

      setDeveloperNotes([]);
      setRenderOrder([{ type: 'section', id: defaultSectionId }]);
    }
    setIsPreviewMode(false);
  }, [initialData, convertBlueprintDataToSectionsLocal]);

  // Export current state as draft data (for persistence across tab switches)
  const getDraftData = useCallback((): TemplateDraftData => {
    return {
      name: blueprintName,
      description: blueprintDescription,
      tags: itemTags,
      types: itemTypes,
      color: blueprintColor,
      sections: sections,
      renderOrder: renderOrder.map(item => item.id),
      owner: owner,
      editPermissions: editPermissions,
      status: blueprintStatus,
    };
  }, [blueprintName, blueprintDescription, itemTags, itemTypes, blueprintColor, sections, renderOrder, owner, editPermissions, blueprintStatus]);

  return {
    // State
    blueprintName,
    blueprintDescription,
    blueprintColor,
    itemTags,
    itemTypes,
    owner,
    editPermissions,
    blueprintStatus,
    sections,
    developerNotes,
    renderOrder,
    isPreviewMode,
    isTagDropdownOpen,
    isTypeDropdownOpen,
    lastAddedSectionId,
    lastAddedFieldId,

    // Setters
    setBlueprintName,
    setBlueprintDescription,
    setBlueprintColor,
    setItemTags,
    setItemTypes,
    setOwner,
    setEditPermissions,
    setBlueprintStatus,
    setSections,
    setDeveloperNotes,
    setRenderOrder,
    setIsPreviewMode,
    setIsTagDropdownOpen,
    setIsTypeDropdownOpen,
    setLastAddedFieldId,

    // Actions
    addSection,
    deleteSection,
    updateSection,
    addField,
    deleteField,
    updateField,
    addDeveloperNote,
    deleteDeveloperNote,
    updateDeveloperNote,
    getBlueprintData,
    getDraftData,
    resetForm,
    resetToInitialData,
    toggleSectionForRole,
    toggleFieldForRole,
    updateSectionVisibility,
    updateFieldVisibility,
    setSectionVisibilityState,
    setFieldVisibilityState,
  };
} 